import {makeAutoObservable, reaction} from "mobx";
import {NumberObservable} from "../../../observables/numberObservable";
import {EventBookingSource} from "../../../../shared/enums/eventBookingSource";

const minSpace = 0;
const maxSpace = 500;

export class EventBookingSpaceDetails {
  totalSpace: NumberObservable;
  reservedSpace: NumberObservable;
  totalBookings: number;
  capacityRemaining: number | null;
  reservedCapacityRemaining: number | null;
  totalStaffAssistedBookings: number;
  totalAppBooking: number;
  totalPaidBookings: number;
  highestBookingCountForSeries: number;
  originalTotalSpace: number = 0;
  originalReservedSpace: number = 0;
  totalWaitlistedBookings: number;

  constructor(
    totalSpace?: number | undefined,
    reservedSpace?: number | undefined,
    totalBookings?: number,
    totalStaffAssistedBookings?: number,
    capacityRemaining?: number | null,
    reservedCapacityRemaining?: number | null,
    totalPaidBookings?: number,
    highestBookingCountForSeries?: number,
    totalWaitlistedBookings?: number
  ) {
    makeAutoObservable(this);
    this.totalBookings = totalBookings ?? 0;
    this.totalPaidBookings = totalPaidBookings ?? 0;
    this.totalStaffAssistedBookings = totalStaffAssistedBookings ?? 0;
    this.totalWaitlistedBookings = totalWaitlistedBookings ?? 0;
    this.capacityRemaining = capacityRemaining ?? null;
    this.reservedCapacityRemaining = reservedCapacityRemaining ?? 0;
    this.totalAppBooking =
      (totalBookings ?? 0) - (totalStaffAssistedBookings ?? 0);
    this.highestBookingCountForSeries = highestBookingCountForSeries ?? 0;
    this.totalSpace = new NumberObservable(
      totalSpace,
      minSpace,
      maxSpace,
      0,
      (value) => {
        value = value ?? undefined;
        const validation =
          value !== undefined &&
          value >= (this.totalSpace.min || 0) && // Using 'this.totalSpace.min' instead of minSpace beacuse the min value changes dynamically
          value <= maxSpace &&
          value >= this.totalBookings;

        // Empty(or 0) space is allowed during initial creation, which is when totalSpace.min is equal to 0.
        // During editing totalSpace.min will be equal to 1
        const isTotalSpaceValid =
          this.totalSpace.min === 0
            ? value === undefined || validation // This will allow undefined when totalSpace.min is 0
            : validation; // This will not allow allow undefined when totalSpace.min is 1

        return this.totalBookings !== 0 && !this.isEventHasNoBookingLimit()
          ? (value ?? 0) >= (this.totalBookings ?? 0) && validation
          : isTotalSpaceValid;
      }
    );

    this.reservedSpace = new NumberObservable(
      reservedSpace,
      minSpace,
      maxSpace,
      0,
      (value) => {
        if (this.reservedSpace.isDirty) {
          let validation =
            value === undefined ||
            (value <= (this.totalSpace.number ?? 0) &&
              value >= minSpace &&
              value <= maxSpace &&
              !this.isStaffBookingMorethanRemainingSpace());
          return this.totalSpace.initialValue &&
            this.reservedSpace.initialValue &&
            this.totalBookings
            ? validation
            : value === undefined || validation;
        }
        return value === undefined;
      },
      true
    );

    reaction(
      () => [this.totalSpace.number, this.reservedSpace.number],
      () => {
        if (this.totalSpace.number) this.reservedSpace.setIsDisabled(false);
        else this.reservedSpace.setIsDisabled(true);
        if (!this.totalSpace.number && !this.totalBookings)
          this.reservedSpace.setNumber(undefined);
        this.reservedSpace.setIsDirty(true);

        this.reservedSpace.max =
          this.totalSpace.number ?? 0 - this.totalAppBooking;
      }
    );

    this.originalTotalSpace = this.totalSpace.number ?? 0;
    this.originalReservedSpace = this.reservedSpace.number ?? 0;
  }

  isStaffSpaceMoreThanCapacity() {
    return (this.reservedSpace.number ?? 0) > (this.totalSpace.number ?? 0);
  }

  isStaffBookingMorethanRemainingSpace() {
    return (
      (this.reservedSpace.number ?? 0) >
        (this.totalSpace.number ?? 0) - this.totalAppBooking ?? 0
    );
  }

  isEventHasNoBookingLimit = () => {
    return (
      this.totalSpace.initialValue === null &&
      this.reservedSpace.initialValue === null &&
      this.totalBookings &&
      this.totalStaffAssistedBookings != null
    );
  };

  isCapacityLessThanConfirmBooking() {
    return (this.totalSpace.number ?? 0) < (this.totalBookings ?? 0);
  }

  checkIsDirty(): boolean {
    return this.totalSpace.isDirty || this.reservedSpace.isDirty;
  }

  onBookingAdded(source: EventBookingSource) {
    this.totalBookings++;
    if (this.capacityRemaining && this.capacityRemaining > 0) {
      this.capacityRemaining--;
    }
    if (source === EventBookingSource.Staff) {
      if (
        this.reservedCapacityRemaining &&
        this.reservedCapacityRemaining > 0
      ) {
        this.reservedCapacityRemaining--;
      }
      this.totalStaffAssistedBookings++;
    } else {
      this.totalAppBooking++;
    }
  }

  onBookingRemoved(source: EventBookingSource) {
    // only add capacity if we have fixed capacity
    if ((this.totalSpace.number ?? 0) > 0) {
      this.totalBookings = Math.max(0, this.totalBookings - 1);
      if (this.capacityRemaining) {
        this.capacityRemaining++;
      }
      if (source === EventBookingSource.Staff) {
        // Only add staff capacity if we had some to begin with
        if (
          this.reservedCapacityRemaining &&
          (this.reservedSpace.number ?? 0) > 0
        ) {
          this.reservedCapacityRemaining++;
        }
        this.totalStaffAssistedBookings = Math.max(
          0,
          this.totalStaffAssistedBookings - 1
        );
      } else {
        this.totalAppBooking = Math.max(0, this.totalAppBooking - 1);
      }
    }
  }

  get isValid(): boolean {
    return (
      !this.checkIsDirty ||
      (this.totalSpace.isValid && this.reservedSpace.isValid)
    );
  }

  get isUnlimitedCapacity(): boolean {
    return (
      this.totalSpace.number === undefined || this.totalSpace.number === null
    );
  }

  get totalSpaceBelowHighestBookingCountForSeries(): boolean {
    return (this.totalSpace.number ?? 0) < this.highestBookingCountForSeries;
  }
}
